import { mapGetters, mapMutations } from 'vuex'
import cognitoService from '@/utils/cognito'
import axios from 'axios'

export default {
  name: 'SignupMini',
  data () {
    return {
      valid: false,
      name: '',
      email: '',
      password: '',
      showPassword: false,
      loading: false,
      error: null,
      success: null,
      showVerification: false,
      verificationCode: '',
      resendLoading: false,
      nameRules: [
        v => !!v || 'Name is required',
        v => v.length >= 2 || 'Name must be at least 2 characters'
      ],
      emailRules: [
        v => !!v || 'Email is required',
        v => /.+@.+\..+/.test(v) || 'Email must be valid'
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => v.length >= 8 || 'Password must be at least 8 characters',
        v => /[A-Z]/.test(v) || 'Password must contain an uppercase letter',
        v => /[a-z]/.test(v) || 'Password must contain a lowercase letter',
        v => /[0-9]/.test(v) || 'Password must contain a number',
        v => /[^A-Za-z0-9]/.test(v) || 'Password must contain a special character'
      ]
    }
  },
  computed: {
    ...mapGetters([
      'apiUrl'
    ])
  },
  methods: {
    ...mapMutations([
      'setAuthToken'
    ]),

    signUpWithGoogle () {
      const next = encodeURIComponent(window.location.origin + '/auth/in?token=')
      window.location.href = `${this.apiUrl}/auth/cognito?next=${next}&type=mini&identity_provider=Google`
    },

    async signUpWithEmail () {
      if (!this.$refs.signupForm.validate()) return

      this.loading = true
      this.error = null

      try {
        // Check if email already exists in our database
        const checkResponse = await axios.post(`${this.apiUrl}/auth/check-email`, {
          email: this.email
        })

        if (checkResponse.data.exists) {
          if (checkResponse.data.providers.google) {
            this.error = 'An account with this email already exists. Please sign in with Google instead.'
            this.loading = false
            return
          }
          if (checkResponse.data.providers.cognito) {
            this.error = 'An account with this email already exists. Please sign in instead.'
            this.loading = false
            return
          }
        }

        // Proceed with Cognito signup
        await cognitoService.signUp(this.email, this.password, this.name)
        this.showVerification = true
        this.success = null
      } catch (err) {
        console.error('Sign up error:', err)
        if (err.code === 'UsernameExistsException') {
          this.error = 'An account with this email already exists. Please sign in instead.'
        } else if (err.code === 'InvalidPasswordException') {
          this.error = 'Password does not meet requirements. Please use at least 8 characters with uppercase, lowercase, number, and symbol.'
        } else {
          this.error = err.message || 'Sign up failed. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    async verifyEmail () {
      if (!this.verificationCode) return

      this.loading = true
      this.error = null

      try {
        await cognitoService.confirmSignUp(this.email, this.verificationCode)

        // Now sign in the user
        const tokens = await cognitoService.signIn(this.email, this.password)

        // Exchange Cognito tokens for our JWT session token
        const response = await axios.post(`${this.apiUrl}/auth/cognito/token`, {
          accessToken: tokens.accessToken,
          idToken: tokens.idToken,
          refreshToken: tokens.refreshToken,
          type: 'mini'
        })

        if (response.data.token) {
          this.setAuthToken(response.data.token)
          this.$router.push('/workspace')
        } else {
          throw new Error('No token received')
        }
      } catch (err) {
        console.error('Verification error:', err)
        if (err.code === 'CodeMismatchException') {
          this.error = 'Invalid verification code. Please try again.'
        } else if (err.code === 'ExpiredCodeException') {
          this.error = 'Verification code has expired. Please request a new one.'
        } else {
          this.error = err.message || 'Verification failed. Please try again.'
        }
      } finally {
        this.loading = false
      }
    },

    async resendCode () {
      this.resendLoading = true
      this.error = null

      try {
        await cognitoService.resendConfirmationCode(this.email)
        this.success = 'Verification code sent! Check your email.'
      } catch (err) {
        console.error('Resend code error:', err)
        this.error = err.message || 'Failed to resend code. Please try again.'
      } finally {
        this.resendLoading = false
      }
    }
  }
}
